import {useCallback, useEffect, useState} from "react";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {
    getPendenciasDoSistema,
    getPlano,
    getVersao,
    listLicencasDoPlano,
    listModulosDoSistema,
    listVersoes
} from "./PlanosComerciaisService";

const valorDe = (response) => response?.data?.valor ?? null;

// Módulos do sistema que têm pendência que bloqueia no catálogo: não entram em versão publicada (decisão de 18/10).
const bloqueiosPorModulo = (pendencias) => (pendencias?.itens || [])
    .filter((item) => item.grau === 'bloqueia' && item.alvo?.tipo === 'modulo')
    .reduce((acc, item) => {
        (acc[item.alvo.id] = acc[item.alvo.id] || []).push(item.mensagem);
        return acc;
    }, {});

/*
 * Tudo o que a página de uma versão do plano mostra. O plano e as versões vêm do app-funcionalidades; os módulos e
 * as pendências, do catálogo do sistema; as assinaturas, do app-licencas. Falhar o que é acessório (pendências,
 * assinaturas) não derruba a página.
 */
export const usePlanoVersao = (planoId, numero) => {
    const [estado, setEstado] = useState({
        carregando: true,
        erro: '',
        plano: null,
        versoes: [],
        versao: null,
        modulos: [],
        bloqueios: {},
        licencas: null,
    });

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, carregando: true, erro: ''}));
        try {
            const [planoResp, versoesResp, versaoResp] = await Promise.all([
                getPlano(planoId),
                listVersoes(planoId),
                // Versão que não existe é 404: a página diz isso, em vez de falhar inteira.
                numero ? getVersao(planoId, numero).catch((error) => {
                    if (error?.response?.status === 404) return null;
                    throw error;
                }) : null,
            ]);
            const plano = valorDe(planoResp);
            const versoes = valorDe(versoesResp)?.versoes || [];
            const versao = valorDe(versaoResp);
            const [modulos, pendencias, licencas] = await Promise.allSettled([
                listModulosDoSistema(plano.id_sistema),
                getPendenciasDoSistema(plano.id_sistema),
                listLicencasDoPlano(planoId),
            ]);
            setEstado({
                carregando: false,
                erro: '',
                plano,
                versoes,
                versao,
                modulos: modulos.status === 'fulfilled' ? modulos.value : [],
                bloqueios: pendencias.status === 'fulfilled' ? bloqueiosPorModulo(valorDe(pendencias.value)) : {},
                licencas: licencas.status === 'fulfilled' ? licencas.value : null,
            });
        } catch (error) {
            setEstado((atual) => ({
                ...atual,
                carregando: false,
                erro: getBackendErrorMessage(error, 'Não foi possível carregar o plano.'),
            }));
        }
    }, [numero, planoId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // A versão que a API devolveu numa gravação (PUT do rascunho): troca sem recarregar a página toda.
    const trocarVersao = useCallback((versao) => setEstado((atual) => ({
        ...atual,
        versao: {...atual.versao, ...versao},
        versoes: atual.versoes.map((item) => (item.numero === versao.numero ? {...item, ...versao} : item)),
    })), []);

    return {...estado, recarregar: carregar, trocarVersao};
};
